import * as React from 'react'

import { Text, spacing, Orientation } from '@thg-commerce/gravity-theme'
import { styled } from '@thg-commerce/gravity-elements/theme'
import { useTheme } from '@thg-commerce/gravity-patterns/theme'
import { ProductBlockPriceDisplayStyle } from '@thg-commerce/gravity-patterns/ProductBlock/theme'

export interface ProductPriceProps {
  price: {
    defaultPrice: string
    minPrice?: string | null
    maxPrice?: string | null
  }
  rrp?: {
    defaultPrice?: string | null
    minPrice?: string | null
    maxPrice?: string | null
  } | null
  freeText?: string
  rrpText?: string
  discountAmount?: string
  vipPrice?: string
  hasFromPrice?: boolean
  fromText?: string
  className?: string
  saveUpToText?: string
  maxSavings?: string
}

const Container = styled.div<{
  displayStyle?: string
  orientation?: Orientation
}>`
  display: flex;
  align-items: ${(props) =>
    props.orientation === Orientation.HORIZONTAL ? 'flex-end' : 'flex-start'};
  flex-direction: ${(props) =>
    props.orientation === Orientation.HORIZONTAL ? 'row' : 'column'};
  ${(props) =>
    Text(
      props.theme.elements.productPrice.priceWithRRP.entry,
      props.theme.elements.productPrice.priceWithRRP.style,
    )};
  ${(props) =>
    props.orientation === Orientation.HORIZONTAL && `gap: ${spacing(1)};`}
`

const Price = styled.div<{
  hasRrp: boolean
}>`
  ${(props) =>
    Text(
      props.hasRrp
        ? props.theme.elements.productPrice.priceWithRRP.entry
        : props.theme.elements.productPrice.price.entry,
      props.hasRrp
        ? props.theme.elements.productPrice.priceWithRRP.style
        : props.theme.elements.productPrice.price.style,
    )}
  color: ${(props) =>
    props.hasRrp
      ? props.theme.elements.productPrice.priceWithRRP.textColor
      : props.theme.elements.productPrice.price.textColor};
`

const RrpContainer = styled.div`
  ${Text('xsmall', 'default')};
  display: flex;
  align-items: center;
`

const RrpPrice = styled.div`
  ${(props) =>
    Text(
      props.theme.elements.productPrice.rrp.entry,
      props.theme.elements.productPrice.rrp.style,
    )};
  color: ${(props) => props.theme.elements.productPrice.rrp.textColor};
  text-decoration: ${(props) =>
    props.theme.elements.productPrice.rrp.textDecoration};
`

const RrpText = styled.span`
  margin-right: ${spacing(0.5)};
`

const SaveUpToContainer = styled.div`
  ${(props) =>
    Text(
      props.theme.elements.productPrice.saveUpTo.entry,
      props.theme.elements.productPrice.saveUpTo.style,
    )};
  color: ${(props) => props.theme.elements.productPrice.saveUpTo.textColor};
  text-decoration: ${(props) =>
    props.theme.elements.productPrice.saveUpTo.textDecoration};
`

const createPriceValue = (config: {
  displayStyle: ProductBlockPriceDisplayStyle
  defaultPrice?: string | null
  minPrice?: string | null
  maxPrice?: string | null
}) => {
  if (!config.minPrice || !config.maxPrice) {
    return config.defaultPrice || null
  }
  switch (config.displayStyle) {
    case ProductBlockPriceDisplayStyle.PRICE_RANGE:
      return config.minPrice === config.maxPrice
        ? config.minPrice
        : `${config.minPrice} - ${config.maxPrice}`
    case ProductBlockPriceDisplayStyle.DEFAULT_PRICE:
    default:
      return config.defaultPrice || null
  }
}

export const ProductPrice = ({
  freeText,
  price,
  rrp,
  rrpText,
  hasFromPrice,
  fromText,
  className,
  saveUpToText,
  maxSavings,
}: ProductPriceProps) => {
  if (freeText) {
    return <Container className={className}>{freeText}</Container>
  }

  const theme = useTheme()
  const {
    displayStyle,
    orientation,
    displayRRPText,
    displayRRPValue,
    displayFromText,
  } = theme.patterns.productBlock.price

  const priceValue = createPriceValue({
    ...price,
    displayStyle,
  })
  const rrpValue = rrp?.defaultPrice
    ? createPriceValue({
        ...rrp,
        displayStyle,
      })
    : undefined

  const showRRPTextConfig = displayRRPText || true

  const showRRPValueConfig = displayRRPValue || true

  const showFromTextConfig = displayFromText || false

  const fromPriceText = showFromTextConfig ? fromText : ''

  return (
    <Container
      className={className}
      displayStyle={displayStyle}
      orientation={orientation}
    >
      {hasFromPrice && <span>{fromText}</span>}
      {rrpValue && (
        <RrpContainer>
          {showRRPTextConfig && <RrpText>{rrpText}</RrpText>}
          {showRRPValueConfig && (
            <RrpPrice data-testid="rrp-price">{rrpValue}</RrpPrice>
          )}
        </RrpContainer>
      )}
      {priceValue && (
        <Price hasRrp={!!rrpValue} data-testid="sale-price">
          {fromPriceText} {priceValue}
        </Price>
      )}
      {Boolean(maxSavings) && (
        <SaveUpToContainer data-testid="savings-price">
          {saveUpToText} {maxSavings}
        </SaveUpToContainer>
      )}
    </Container>
  )
}
